import React from 'react';
import { Link } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Button } from '../components/ui/button';
import { 
  Users, 
  Target, 
  TrendingUp, 
  MessageSquare, 
  Award,
  CheckCircle,
  ArrowRight,
  Phone
} from 'lucide-react';

const ManagementLeadershipPage = () => {
  const problematiques = [
    {
      icon: Users,
      title: "Difficultés à mobiliser les équipes",
      description: "Vos collaborateurs semblent désengagés et la motivation collective s'essouffle."
    },
    {
      icon: Target,
      title: "Gestion des conflits complexe",
      description: "Les tensions interpersonnelles freinent la productivité et l'ambiance de travail."
    },
    {
      icon: TrendingUp,
      title: "Passage du manager au leader",
      description: "Vous souhaitez inspirer plutôt que diriger, mais ne savez pas par où commencer."
    },
    {
      icon: MessageSquare,
      title: "Communication descendante inefficace",
      description: "Vos messages ne passent pas ou sont mal interprétés par vos équipes."
    }
  ];

  const enjeux = [
    "Créer un climat de confiance durable",
    "Développer l'autonomie et la responsabilisation",
    "Favoriser l'intelligence collective",
    "Adapter son style de management aux situations",
    "Accompagner le changement avec sérénité",
    "Renforcer la cohésion d'équipe"
  ];

  const benefices = [
    {
      title: "Leadership authentique",
      description: "Développez un style de management aligné avec vos valeurs et votre personnalité."
    },
    {
      title: "Équipes engagées",
      description: "Créez les conditions de la motivation intrinsèque et de la performance collective."
    },
    {
      title: "Résolution de conflits",
      description: "Maîtrisez les techniques de médiation et de communication non-violente."
    },
    {
      title: "Agilité managériale",
      description: "Adaptez votre posture aux différentes situations et personnalités."
    }
  ];

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#E9DFCF' }} data-testid="management-page">
      <Header />
      
      <main>
        {/* Hero Section */}
        <section className="py-16 md:py-24 bg-gradient-to-b from-blue-700 to-blue-800">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-3xl">
              <span className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium mb-6" style={{ backgroundColor: '#D8C9B3', color: '#2C2A27' }}>
                <Award className="w-4 h-4" />
                Délivrance d'une attestation précisant les compétences visées
              </span>
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-black text-white mb-6" style={{ letterSpacing: '-0.04em' }}>
                Management et Leadership – L'art de fédérer
              </h1>
              <p className="text-xl text-blue-100 mb-8">
                Devenez le leader que vos équipes méritent.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link to="/#formulaire">
                  <Button 
                    className="text-white font-semibold py-4 px-8 rounded-full transition-all duration-300 ease-out hover:-translate-y-0.5 active:scale-[0.98] active:translate-y-0"
                    style={{ 
                      backgroundColor: '#1C2E4A',
                      boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
                    }}
                    onMouseEnter={(e) => {
                      e.target.style.backgroundColor = '#0F1E33';
                      e.target.style.boxShadow = '0 8px 16px rgba(0, 0, 0, 0.15)';
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.backgroundColor = '#1C2E4A';
                      e.target.style.boxShadow = '0 4px 6px rgba(0, 0, 0, 0.1)';
                    }}
                  >
                    Demander un diagnostic stratégique
                    <ArrowRight className="ml-2 w-5 h-5" />
                  </Button>
                </Link>
                <a href="tel:0678269110">
                  <Button 
                    variant="outline" 
                    className="border-white text-white font-medium py-4 px-8 rounded-full transition-all duration-300 ease-out hover:bg-white/10 hover:-translate-y-0.5 active:scale-[0.98] active:translate-y-0"
                  >
                    <Phone className="mr-2 w-5 h-5" />
                    Être rappelé
                  </Button>
                </a>
              </div>
            </div>
          </div>
        </section>

        {/* Problématiques terrain */}
        <section className="py-16 md:py-24" style={{ backgroundColor: '#E9DFCF' }}>
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-3xl mb-12">
              <h2 className="text-3xl md:text-4xl font-extrabold text-gray-900 mb-4" style={{ letterSpacing: '-0.04em' }}>
                Problématiques terrain
              </h2>
              <p className="text-lg text-gray-600">
                Vous reconnaissez-vous dans ces situations ?
              </p>
            </div>
            
            <div className="grid md:grid-cols-2 gap-6">
              {problematiques.map((item, index) => (
                <div key={index} className="bg-white p-6 rounded-xl border border-gray-200 hover:shadow-lg transition-shadow">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-blue-50 rounded-xl flex items-center justify-center flex-shrink-0">
                      <item.icon className="w-6 h-6 text-blue-700" />
                    </div>
                    <div>
                      <h3 className="font-bold text-gray-900 mb-2">{item.title}</h3>
                      <p className="text-gray-600 text-sm">{item.description}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Enjeux humains et organisationnels */}
        <section className="py-16 md:py-24 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid md:grid-cols-2 gap-12 items-center">
              <div>
                <h2 className="text-3xl md:text-4xl font-extrabold text-gray-900 mb-6" style={{ letterSpacing: '-0.04em' }}>
                  Enjeux humains et organisationnels
                </h2>
                <p className="text-lg text-gray-600 mb-8">
                  Le management moderne exige bien plus que des compétences techniques. 
                  Il s'agit de créer un environnement où chacun peut donner le meilleur de lui-même.
                </p>
                <ul className="space-y-4">
                  {enjeux.map((enjeu, index) => (
                    <li key={index} className="flex items-center gap-3">
                      <div className="w-6 h-6 bg-green-100 rounded-full flex items-center justify-center">
                        <CheckCircle className="w-4 h-4 text-green-600" />
                      </div>
                      <span className="text-gray-700">{enjeu}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <div className="bg-gray-50 rounded-2xl p-8">
                <img 
                  src="https://images.pexels.com/photos/8117466/pexels-photo-8117466.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940" 
                  alt="Équipe en réunion collaborative"
                  className="rounded-xl w-full h-64 object-cover mb-6"
                  loading="lazy"
                />
                <p className="text-gray-600 text-center italic">
                  "Le leadership, c'est l'art d'amener les autres à vouloir faire ce qui doit être fait."
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Notre approche pédagogique */}
        <section className="py-16 md:py-24" style={{ backgroundColor: '#E9DFCF' }}>
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-3xl mb-12">
              <h2 className="text-3xl md:text-4xl font-extrabold text-gray-900 mb-4" style={{ letterSpacing: '-0.04em' }}>
                Notre approche pédagogique
              </h2>
              <p className="text-lg text-gray-600">
                Une méthode éprouvée, ancrée dans la réalité de votre entreprise.
              </p>
            </div>
            
            <div className="grid md:grid-cols-3 gap-8">
              <div className="bg-white p-8 rounded-xl border border-gray-200">
                <div className="w-12 h-12 bg-blue-700 rounded-xl flex items-center justify-center text-white font-bold mb-4">
                  1
                </div>
                <h3 className="font-bold text-gray-900 mb-2">Diagnostic personnalisé</h3>
                <p className="text-gray-600 text-sm">
                  Analyse de votre contexte, vos enjeux et vos objectifs spécifiques.
                </p>
              </div>
              <div className="bg-white p-8 rounded-xl border border-gray-200">
                <div className="w-12 h-12 bg-blue-700 rounded-xl flex items-center justify-center text-white font-bold mb-4">
                  2
                </div>
                <h3 className="font-bold text-gray-900 mb-2">Mises en situation</h3>
                <p className="text-gray-600 text-sm">
                  Exercices pratiques, jeux de rôle et cas concrets de votre quotidien.
                </p>
              </div>
              <div className="bg-white p-8 rounded-xl border border-gray-200">
                <div className="w-12 h-12 bg-blue-700 rounded-xl flex items-center justify-center text-white font-bold mb-4">
                  3
                </div>
                <h3 className="font-bold text-gray-900 mb-2">Suivi post-formation</h3>
                <p className="text-gray-600 text-sm">
                  Accompagnement terrain pour ancrer les nouvelles pratiques.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Bénéfices concrets */}
        <section className="py-16 md:py-24 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-3xl mb-12">
              <h2 className="text-3xl md:text-4xl font-extrabold text-gray-900 mb-4" style={{ letterSpacing: '-0.04em' }}>
                Bénéfices concrets
              </h2>
              <p className="text-lg text-gray-600">
                Des résultats mesurables pour vous et vos équipes.
              </p>
            </div>
            
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {benefices.map((benefice, index) => (
                <div key={index} className="bg-gray-50 p-6 rounded-xl">
                  <h3 className="font-bold text-gray-900 mb-2">{benefice.title}</h3>
                  <p className="text-gray-600 text-sm">{benefice.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-16 md:py-24 bg-blue-700">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="text-3xl md:text-4xl font-extrabold text-white mb-6" style={{ letterSpacing: '-0.04em' }}>
              Prêt à transformer votre management ?
            </h2>
            <p className="text-xl text-blue-100 mb-8">
              Demandez un diagnostic stratégique gratuit et personnalisé.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/#formulaire">
                <Button 
                  className="bg-white text-blue-700 font-semibold py-4 px-8 rounded-full shadow-lg transition-all duration-[250ms] ease-out hover:bg-gray-100 hover:-translate-y-0.5 hover:shadow-xl active:scale-[0.98] active:translate-y-0"
                >
                  Demander un diagnostic stratégique
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </Link>
              <a href="tel:0678269110">
                <Button 
                  variant="outline" 
                  className="border-white text-white font-medium py-4 px-8 rounded-full transition-all duration-[250ms] ease-out hover:bg-white/10 hover:-translate-y-0.5 active:scale-[0.98] active:translate-y-0"
                >
                  <Phone className="mr-2 w-5 h-5" />
                  Être rappelé
                </Button>
              </a>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default ManagementLeadershipPage;
